/**
 * R66 APP Web 容器协议
 * 核心对齐 store_sd_frontend/src/utils/JsBridge.js
 * 业务 API 对齐 store_sd_frontend/src/composables/useAppBridge.js
 *
 * 传输：window.smartBridge.call + window._handleCallback（非文档中的 window.JsBridge）
 * 旧协议：callLegacy → Android/Harmony nativeBridge / iOS WebViewJavascriptBridge
 *
 * 用法（等同 Nuxt useAppBridge）：
 *   AppWebBridge.getUserInfo().then(console.log).catch(console.error);
 *   AppWebBridge.call('getMallToken', null, (res, err) => {});
 *   AppWebBridge.getAppVersion();
 *   AppWebBridge.isAppVersionAtLeast('4.66.00');
 *   AppWebBridge.routeBindEmail();
 *   AppWebBridge.callLegacy('statCommonData');
 */
(function (global) {
  'use strict';

  var ROUTE_BIND_EMAIL = 'smart://profile/bindemail';
  var ROUTE_LIVE_CHAT = 'smart://misc/livechat';

  var _dispatchRef = null;

  // ---------- 版本比较（对齐 appVersion.js） ----------
  function toVersionSegments(version) {
    var s = String(version != null ? version : '').trim();
    if (!s) return [0];
    return s.split('.').map(function (part) {
      var n = parseInt(part, 10);
      return isNaN(n) ? 0 : n;
    });
  }

  function compareAppVersion(a, b) {
    if (typeof global.compareAppVersion === 'function') {
      return global.compareAppVersion(a, b);
    }
    var aa = toVersionSegments(a);
    var bb = toVersionSegments(b);
    var len = Math.max(aa.length, bb.length);
    for (var i = 0; i < len; i++) {
      var x = aa[i] != null ? aa[i] : 0;
      var y = bb[i] != null ? bb[i] : 0;
      if (x < y) return -1;
      if (x > y) return 1;
    }
    return 0;
  }

  function isAppVersionAtLeastSync(current, minVersion) {
    if (typeof global.isAppVersionAtLeast === 'function') {
      return global.isAppVersionAtLeast(current, minVersion);
    }
    return compareAppVersion(current, minVersion) >= 0;
  }

  function isAppVersionBetweenSync(current, minVersion, maxVersion, inclusiveMax) {
    if (compareAppVersion(current, minVersion) < 0) return false;
    var upper = compareAppVersion(current, maxVersion);
    if (inclusiveMax !== false) return upper <= 0;
    return upper < 0;
  }

  // ---------- JsBridge（对齐 JsBridge.js） ----------
  function JsBridgeCore() {
    this.cbMap = new Map();
    this.bridge = null;
    this.platform = this.detectPlatform();
    this.init();
  }

  JsBridgeCore.prototype.detectPlatform = function () {
    var ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/RR_MALL_IOS/i.test(ua)) return 'ios';
    if (/RR_MALL_Android/i.test(ua)) return 'android';
    if (/RR_MALL_Harmony/i.test(ua)) return 'harmony';
    return 'h5';
  };

  JsBridgeCore.prototype.ensureHandleCallback = function () {
    var self = this;

    if (global._handleCallback === _dispatchRef && _dispatchRef) {
      return;
    }

    var previous = global._handleCallback;
    if (previous === _dispatchRef) {
      previous = null;
    }

    function dispatch(id, resultStr) {
      var fn = self.cbMap.get(id);
      if (fn) {
        self.cbMap.delete(id);
        var res;
        try {
          res = typeof resultStr === 'string' ? JSON.parse(resultStr) : resultStr;
        } catch (e) {
          res = { isSuccess: false, error: resultStr };
        }
        if (res && res.isSuccess) fn(res.result, null);
        else fn(null, (res && res.error) || 'bridge error');
        return;
      }
      if (typeof previous === 'function') {
        previous(id, resultStr);
      }
    }

    _dispatchRef = dispatch;
    global._handleCallback = dispatch;
  };

  JsBridgeCore.prototype.init = function () {
    if (this.platform === 'h5') return;

    this.bridge = global.smartBridge;
    if (!this.bridge) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[bridge] window.smartBridge 未注入');
      }
      return;
    }

    this.ensureHandleCallback();
  };

  JsBridgeCore.prototype.setupIOSBridge = function (callback) {
    if (global.WebViewJavascriptBridge) {
      return callback(global.WebViewJavascriptBridge);
    }
    if (global.WVJBCallbacks) {
      return global.WVJBCallbacks.push(callback);
    }
    global.WVJBCallbacks = [callback];
    var iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = 'wvjbscheme://__BRIDGE_LOADED__';
    document.documentElement.appendChild(iframe);
    setTimeout(function () {
      document.documentElement.removeChild(iframe);
    }, 0);
  };

  JsBridgeCore.prototype.call = function (name, params, callback) {
    if (this.platform === 'h5') {
      callback(null, 'NOT_SUPPORT');
      return;
    }

    if (!this.bridge) {
      this.bridge = global.smartBridge;
    }

    var bridge = this.ensureBridge();
    if (!bridge) {
      throw new Error('[bridge] smartBridge 未定义');
    }

    this.ensureHandleCallback();

    var isSupport =
      typeof bridge.isSupportProtocol === 'function'
        ? bridge.isSupportProtocol(name)
        : false;
    if (!isSupport) {
      callback(null, 'NOT_SUPPORT');
      return;
    }

    var realParams = null;
    if (params !== null) {
      realParams = JSON.stringify(params);
    }

    var id = Date.now() + '_' + Math.random().toString(36).slice(2);
    this.cbMap.set(id, callback);
    bridge.call(name, realParams, id);
  };

  JsBridgeCore.prototype.callLegacy = function (name, params) {
    if (params === undefined) params = null;
    if (this.platform === 'h5') return;

    if (this.platform === 'android' || this.platform === 'harmony') {
      var nb = global.nativeBridge;
      if (!nb || typeof nb[name] !== 'function') return undefined;
      if (params === null) return nb[name]();
      return nb[name](params);
    }

    if (this.platform === 'ios') {
      var self = this;
      return new Promise(function (resolve) {
        self.setupIOSBridge(function (bridge) {
          bridge.callHandler(name, params != null ? params : '', function (responseData) {
            resolve(responseData);
          });
        });
      });
    }
  };

  JsBridgeCore.prototype.isAndroid = function () {
    return this.platform === 'android';
  };

  JsBridgeCore.prototype.isIOS = function () {
    return this.platform === 'ios';
  };

  JsBridgeCore.prototype.isHarmony = function () {
    return this.platform === 'harmony';
  };

  JsBridgeCore.prototype.isApp = function () {
    return this.platform !== 'h5';
  };

  JsBridgeCore.prototype.isSupportProtocol = function (methodName) {
    if (!this.bridge) {
      throw new Error('[bridge] smartBridge 未定义');
    }
    return this.bridge.isSupportProtocol(methodName);
  };

  JsBridgeCore.prototype.isSupportRoute = function (path) {
    var bridge = this.ensureBridge();
    if (!bridge || typeof bridge.isSupportRoute !== 'function') {
      return null;
    }
    return !!bridge.isSupportRoute(path);
  };

  JsBridgeCore.prototype.ensureBridge = function () {
    if (!this.bridge) {
      this.bridge = global.smartBridge;
    }
    return this.bridge;
  };

  JsBridgeCore.prototype.goBack = function () {
    var bridge = this.ensureBridge();
    if (!bridge) {
      throw new Error('[bridge] smartBridge 未定义');
    }
    bridge.goBack();
  };

  JsBridgeCore.prototype.route = function (path, params) {
    var bridge = this.ensureBridge();
    if (!bridge) {
      throw new Error('[bridge] smartBridge 未定义');
    }

    if (typeof bridge.isSupportRoute === 'function') {
      if (!bridge.isSupportRoute(path)) {
        throw new Error('invalid path');
      }
    }

    if (params != null) {
      bridge.route(path, JSON.stringify(params));
    } else {
      bridge.route(path);
    }
  };

  var _nativeBridge = new JsBridgeCore();

  /** 对齐 useAppBridge：call → Promise */
  function callNative(name, params) {
    return new Promise(function (resolve, reject) {
      try {
        _nativeBridge.call(name, params, function (res, error) {
          error != null && error !== '' ? reject(error) : resolve(res);
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  function callLegacyNative(name, params) {
    return Promise.resolve(_nativeBridge.callLegacy(name, params != null ? params : null));
  }

  /** getMallTokenV2 入参：{ id?, refresh?, type, host }；id 可选；type 固定 CLEANING；host 为当前 location.host；refresh 省略视为 false */
  function normalizeMallTokenV2Params(params) {
    var p = params != null && typeof params === 'object' ? params : {};
    var payload = { type: 'CLEANING' };
    if (global.location && global.location.host) {
      payload.host = global.location.host;
    }
    if (p.id != null && p.id !== '') {
      var id = parseInt(p.id, 10);
      if (!isNaN(id)) payload.id = id;
    }
    if (p.refresh === true) {
      payload.refresh = true;
    }
    return payload;
  }

  // ---------- 业务 API（对齐 useAppBridge.js） ----------
  var AppWebBridge = {
    ROUTE_BIND_EMAIL: ROUTE_BIND_EMAIL,
    ROUTE_LIVE_CHAT: ROUTE_LIVE_CHAT,

    /** Nuxt jsBridge / $nativeBridge 等价 */
    core: _nativeBridge,
    nativeBridge: _nativeBridge,

    reinit: function () {
      _nativeBridge.bridge = global.smartBridge || _nativeBridge.bridge;
      _nativeBridge.init();
      return AppWebBridge.isBridgeReady();
    },

    /** 直接 route，与 smartBridge.route(path) 一致；无 params 时不传第二参 */
    routeNative: function (path, params) {
      var bridge = global.smartBridge;
      if (!bridge || typeof bridge.route !== 'function') {
        throw new Error('[bridge] smartBridge.route 未定义');
      }
      if (params != null) {
        bridge.route(path, typeof params === 'string' ? params : JSON.stringify(params));
      } else {
        bridge.route(path);
      }
    },

    /** 调试：当前 _handleCallback 是否由 R66 分发器接管 */
    isHandleCallbackReady: function () {
      return global._handleCallback === _dispatchRef && !!_dispatchRef;
    },

    isBridgeReady: function () {
      return !!(_nativeBridge.bridge && typeof _nativeBridge.bridge.call === 'function');
    },

    getPlatform: function () {
      return _nativeBridge.platform;
    },
    detectPlatform: function () {
      return _nativeBridge.platform;
    },
    isAndroid: function () {
      return _nativeBridge.isAndroid();
    },
    isIOS: function () {
      return _nativeBridge.isIOS();
    },
    isHarmony: function () {
      return _nativeBridge.isHarmony();
    },
    isApp: function () {
      return _nativeBridge.isApp();
    },

    /** 底层 call：支持 callback 或 Promise */
    call: function (name, params, callback) {
      var p = params !== undefined ? params : null;
      if (typeof callback === 'function') {
        try {
          _nativeBridge.call(name, p, callback);
        } catch (e) {
          callback(null, e);
        }
        return;
      }
      return callNative(name, p);
    },

    callLegacy: function (name, params) {
      return callLegacyNative(name, params);
    },

    isSupportProtocol: function (methodName) {
      return _nativeBridge.isSupportProtocol(methodName);
    },
    isSupportRoute: function (path) {
      return _nativeBridge.isSupportRoute(path);
    },
    isSupportRouteBindEmail: function () {
      var v = _nativeBridge.isSupportRoute(ROUTE_BIND_EMAIL);
      return v === true;
    },
    isSupportRouteLiveChat: function () {
      var v = _nativeBridge.isSupportRoute(ROUTE_LIVE_CHAT);
      return v === true;
    },

    goBack: function () {
      _nativeBridge.goBack();
    },
    route: function (path, params) {
      _nativeBridge.route(path, params);
    },
    routeBindEmail: function () {
      _nativeBridge.route(ROUTE_BIND_EMAIL);
    },
    routeLiveChat: function () {
      _nativeBridge.route(ROUTE_LIVE_CHAT);
    },

    getAppMallToken: function () {
      return callNative('getMallToken', null);
    },
    getMallToken: function (params) {
      return callNative('getMallToken', params != null ? params : null);
    },
    getMallTokenV2: function (params) {
      return callNative('getMallTokenV2', normalizeMallTokenV2Params(params));
    },

    getUserInfo: function () {
      return callNative('getUserInfo', null);
    },
    getAppInfo: function () {
      return callNative('getAppInfo', null);
    },
    /** R66 无参。回调 result 为 { scene }：DISCOVER=发现页，SERVICE_CENTER=服务中心。 */
    getCurrentScene: function () {
      return callNative('getCurrentScene', null);
    },

    hasPermission: function (permission) {
      return callNative('hasPermission', { permission: permission });
    },
    requestPermission: function (permission) {
      return callNative('requestPermission', { permission: permission });
    },

    toggleTabBarVisible: function (visible) {
      return callNative('setBottomNavBarVisible', { visible: !!visible });
    },
    setBottomNavBarVisible: function (visible) {
      return callNative('setBottomNavBarVisible', { visible: !!visible });
    },

    getStatusBarHeight: function () {
      return callNative('getStatusBarHeight', null);
    },
    scanQRCode: function () {
      return callNative('scanQRCode', {});
    },

    isRecommendAgree: function () {
      return callNative('isRecommendAgree', null);
    },
    setRecommendAgree: function (agree) {
      return callNative('setRecommendAgree', { agree: !!agree });
    },

    getLocation: function () {
      return callNative('getLocation', null);
    },
    updateContainerUIConfig: function (config) {
      return callNative(
        'updateContainerUIConfig',
        config != null ? config : { statusBarVisible: true }
      );
    },

    setWebViewOriginY: function (height) {
      return callLegacyNative('setWebViewOriginY', height).then(function (res) {
        return res != null ? res : true;
      });
    },
    statCommonData: function () {
      return callLegacyNative('statCommonData', null).then(function (res) {
        return res != null ? res : true;
      });
    },

    compareAppVersion: compareAppVersion,

    getAppVersion: function () {
      if (!_nativeBridge.isApp()) return Promise.resolve(null);
      return AppWebBridge.getAppInfo()
        .then(function (info) {
          return info && info.app_version != null ? info.app_version : null;
        })
        .catch(function () {
          return null;
        });
    },

    isAppVersionAtLeast: function (minVersion) {
      return AppWebBridge.getAppVersion().then(function (v) {
        if (v == null) return false;
        return isAppVersionAtLeastSync(v, minVersion);
      });
    },

    isAppVersionBetween: function (minVersion, maxVersion, options) {
      var inclusiveMax = !options || options.inclusiveMax !== false;
      return AppWebBridge.getAppVersion().then(function (v) {
        if (v == null) return false;
        return isAppVersionBetweenSync(v, minVersion, maxVersion, inclusiveMax);
      });
    },

    isAppVersionAtLeastForPlatform: function (rules) {
      if (!_nativeBridge.isApp() || !rules || typeof rules !== 'object') {
        return Promise.resolve(false);
      }
      return AppWebBridge.getAppInfo()
        .then(function (info) {
          var platform = info && info.platform;
          var min =
            (platform && rules[platform]) != null
              ? rules[platform]
              : rules.default != null
                ? rules.default
                : rules.fallback;
          if (min == null) return false;
          var v = info && info.app_version;
          if (v == null) return false;
          return isAppVersionAtLeastSync(v, min);
        })
        .catch(function () {
          return false;
        });
    },

    matchAppVersionRules: function (rules) {
      if (!_nativeBridge.isApp() || !rules || typeof rules !== 'object') {
        return Promise.resolve(null);
      }
      return AppWebBridge.getAppVersion().then(function (v) {
        if (v == null) return null;
        var out = {};
        Object.keys(rules).forEach(function (key) {
          out[key] = isAppVersionAtLeastSync(v, rules[key]);
        });
        return out;
      });
    },
  };

  global.AppWebBridge = AppWebBridge;
})(typeof window !== 'undefined' ? window : this);
